import { FormSelect } from "@/components/Selector"
import { ClearFilter } from "@/constants/SvgConstants"
import { useMediaQuery } from "@/hooks/useMediaQuery"
import { IOption } from "@/types"
import { cn } from "@/utils"
import { enUS, es, fr, it, pt } from "date-fns/locale"
import type { FC } from "react"
import { memo } from "react"
import DatePicker, { registerLocale } from "react-datepicker"
import { Controller, FormProvider, UseFormReturn, useWatch } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"

import { selectUser, StatusType, useStatusOptions, useStoreClient } from "@usher/pe-client-front-end-lib"
import "react-datepicker/dist/react-datepicker.min.css"

registerLocale("en-US", enUS)
registerLocale("fr-FR", fr)
registerLocale("it-IT", it)
registerLocale("es-ES", es)
registerLocale("pt-PT", pt)

export type FormValues = {
  fiat: IOption
  crypto: IOption
  status: IOption
  period: IOption
  card: IOption
  date: Date | Date[]
}

export type Options = {
  fiat: IOption[]
  crypto: IOption[]
  cards: IOption[]
  status: IOption[]
  period?: IOption[]
}

type Props = {
  onSubmit: (data: any) => void
  onReset: () => void
  options: Options
  formMethods: UseFormReturn<FormValues>
  withoutExchangeTo?: boolean
  withPayoutsCards?: boolean
  type?: string
}

const FilterBaseComponent: FC<Props> = ({ type, formMethods, options, onReset, onSubmit, withoutExchangeTo, withPayoutsCards = false }) => {
  const isIban = type === "IBAN"
  const isMobile = useMediaQuery("(max-width: 768px)")
  const intl = useIntl()
  const { iban_active } = useStoreClient(selectUser)
  const enableStatusQueries = isIban && iban_active
  const { options: ibanStatuses } = useStatusOptions({ type: StatusType.IBAN, enabled: enableStatusQueries })
  const { options: topupStatuses } = useStatusOptions({ type: StatusType.TopUp, enabled: enableStatusQueries })
  const statuses = [...ibanStatuses, ...topupStatuses]
  const variant = isMobile ? "contained" : "text"

  const watchPeriod = useWatch({ control: formMethods.control, name: "period" })

  if (options.period != null && options.crypto != null) {
    options.period[0].label = intl.formatMessage({ id: "all_time" })
    options.period[1].label = intl.formatMessage({ id: "custom" })
    options.crypto[0].label = intl.formatMessage({ id: "all" })
    if (options.cards != null) {
      options.cards[0].label = intl.formatMessage({ id: "all" })
    }
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)} className="form-orders flex-col-reverse md:flex-row">
        <div className={cn("grid grid-cols-1 xs:grid-cols-2 md:flex gap-[14px] w-full md:gap-[40px] child:flex-shrink")}>
          <FormSelect
            variant={variant}
            classNames={{ control: "w-full" }}
            label={<FormattedMessage id="period" />}
            control={formMethods.control}
            name="period"
            options={options.period as IOption[]}
          />
          {withPayoutsCards ? (
            <FormSelect
              variant={variant}
              classNames={{ control: "w-full" }}
              label={<FormattedMessage id="card" />}
              control={formMethods.control}
              name="card"
              options={options.cards ?? []}
            />
          ) : null}
          {watchPeriod.value !== "0" && (
            <div
              className={cn(
                "md:min-w-28 md:max-w-fit flex-1 flex flex-col w-full xs:col-span-2",
                isMobile && watchPeriod.value !== "0" ? "order-7" : "order-none",
              )}
            >
              <Controller
                control={formMethods.control}
                name="date"
                render={({ field: { onChange, value, name } }) => {
                  return (
                    <>
                      <label>
                        <FormattedMessage id="select_period" />
                      </label>
                      <DatePicker
                        name={name}
                        selectsRange
                        selected={Array.isArray(value) ? value[0] : value}
                        startDate={Array.isArray(value) ? value[0] : value}
                        endDate={Array.isArray(value) ? value[1] : value}
                        onChange={onChange}
                        maxDate={new Date()}
                        locale={intl.locale}
                        isClearable
                        className="form-orders_item_date min-w-[250px] w-full max-w-[250px] [& input]:w-full"
                      />
                    </>
                  )
                }}
              />
            </div>
          )}
          {!withoutExchangeTo && (
            <>
              <FormSelect
                label={<FormattedMessage id="exchange_from" />}
                name="fiat"
                classNames={{ control: "w-full" }}
                variant={variant}
                control={formMethods.control}
                options={options.fiat.filter((opt) => opt.label !== "USD")}
                searchable
              />
              <FormSelect
                label={<FormattedMessage id="exchange_to" />}
                classNames={{ control: "w-full" }}
                control={formMethods.control}
                variant={variant}
                name="crypto"
                options={options.crypto.filter((opt) => opt.label !== "USD")}
                searchable
              />
            </>
          )}
          <FormSelect
            label={<FormattedMessage id="status" />}
            classNames={{ control: "w-full" }}
            control={formMethods.control}
            name="status"
            variant={variant}
            options={iban_active && isIban ? [{ label: "All", value: "0" }, ...statuses] : options.status}
            searchable
          />
        </div>
        <div className="form-orders_right">
          <button type="submit" className="form-orders_submit btn-reset btn-sq btn-trpr input-brdr no-decorate">
            <FormattedMessage id="results" />
          </button>
          <button onClick={onReset} className="form-orders_clear btn-reset  no-decorate">
            <ClearFilter />
          </button>
        </div>
      </form>
    </FormProvider>
  )
}

export const FilterBase = memo(FilterBaseComponent)
